import { useEffect, useState } from 'react'
import useAxiosPrivate from '../../hooks/useAxiosPrivate'

const roleLabels = {
  Admin: 'Admin',
  Ngo: 'NGO',
  Org_admin: 'Org Admin',
  Event_admin: 'Event Admin',
  User: 'User',
}

// Admin only. Every change here is saved right away, separately from "Gem ændringer".
export default function UserAccess({ userId, isSelf }) {
  const axiosPrivate = useAxiosPrivate()
  const [access, setAccess] = useState(null)
  const [role, setRole] = useState('')
  const [ngoSlug, setNgoSlug] = useState('')
  const [eventToAdd, setEventToAdd] = useState('')
  const [busy, setBusy] = useState(false)
  const [message, setMessage] = useState(null)
  const base = '/user/' + userId + '/access'

  const apply = (data) => {
    setAccess(data)
    setRole(data.role || 'User')
    setNgoSlug(data.ngo_slug || '')
  }

  useEffect(() => {
    axiosPrivate.get(base)
      .then((res) => apply(res.data))
      .catch((err) => {
        console.error(err)
        setMessage({ error: true, text: 'Kunne ikke hente brugerens rolle og events.' })
      })
    // eslint-disable-next-line
  }, [userId])

  const call = async (request, success) => {
    setBusy(true)
    setMessage(null)
    try {
      const res = await request()
      apply(res.data)
      setMessage({ error: false, text: success })
    } catch (err) {
      console.error(err)
      setMessage({ error: true, text: err?.response?.data?.message || 'Ændringen kunne ikke gemmes.' })
    }
    setBusy(false)
  }

  const saveRole = () => call(
    () => axiosPrivate.put(base + '/role', { role, ...(role === 'Ngo' ? { ngo_slug: ngoSlug } : {}) }),
    'Rollen er gemt.'
  )
  const addEvent = () => eventToAdd && call(
    () => axiosPrivate.post(base + '/events/' + eventToAdd),
    'Eventet er tilknyttet.'
  ).then(() => setEventToAdd(''))
  const togglePermission = (slug, granted) => call(
    () => granted ? axiosPrivate.post(base + '/permissions/' + slug) : axiosPrivate.delete(base + '/permissions/' + slug),
    granted ? 'Permission er givet.' : 'Permission er fjernet.'
  )
  const removeEvent = (slug) => call(
    () => axiosPrivate.delete(base + '/events/' + slug),
    'Eventet er fjernet.'
  )

  if (!access) {
    return message ? <p className="px-8 py-6 text-sm text-red-600">{message.text}</p> : null
  }

  const attached = new Set(access.events.map((e) => e.slug))
  const roleChanged = role !== (access.role || 'User') || (role === 'Ngo' && ngoSlug !== (access.ngo_slug || ''))

  return (
    <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
      <div className="px-8 py-6 border-b border-gray-100">
        <h2 className="text-xl font-semibold text-gray-900">Rolle, permissions og events</h2>
        <p className="text-gray-500 text-sm mt-1">Ændringer her gemmes med det samme.</p>
      </div>

      <div className="px-8 py-6 space-y-6">
        {message && (
          <p className={'text-sm ' + (message.error ? 'text-red-600' : 'text-green-700')}>{message.text}</p>
        )}

        <div>
          <h3 className="text-sm font-semibold text-gray-900 mb-2">Rolle</h3>
          <div className="flex flex-col sm:flex-row sm:items-center gap-3">
            <select
              value={role}
              disabled={isSelf || busy}
              onChange={(e) => setRole(e.target.value)}
              className="block w-full sm:w-48 rounded-md border-gray-300 text-sm focus:border-indigo-500 focus:ring-indigo-500 disabled:bg-gray-100"
            >
              {access.roles.map((r) => (
                <option key={r} value={r}>{roleLabels[r] || r}</option>
              ))}
            </select>
            {role === 'Ngo' && (
              <select
                value={ngoSlug}
                disabled={isSelf || busy}
                onChange={(e) => setNgoSlug(e.target.value)}
                className="block w-full sm:w-56 rounded-md border-gray-300 text-sm focus:border-indigo-500 focus:ring-indigo-500 disabled:bg-gray-100"
              >
                <option value="">Vælg NGO</option>
                {access.all_ngos.map((ngo) => (
                  <option key={ngo.slug} value={ngo.slug}>{ngo.name}</option>
                ))}
              </select>
            )}
            {!isSelf && (
              <button
                type="button"
                onClick={saveRole}
                disabled={busy || !roleChanged || (role === 'Ngo' && !ngoSlug)}
                className="inline-flex justify-center rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
              >
                Gem rolle
              </button>
            )}
          </div>
          {isSelf && <p className="mt-2 text-xs text-gray-500">Du kan ikke ændre din egen rolle.</p>}
          {role === 'Ngo' && !isSelf && (
            <p className="mt-2 text-xs text-gray-500">En NGO-bruger får automatisk alle NGO'ens events tilknyttet.</p>
          )}
        </div>

        {access.all_permissions.length > 0 && (
          <div>
            <h3 className="text-sm font-semibold text-gray-900 mb-1">Ekstra permissions</h3>
            <p className="text-xs text-gray-500 mb-2">Gives oven i rollen. Admin har dem allerede.</p>
            <div className="space-y-2">
              {access.all_permissions.map((permission) => (
                <label key={permission.permission_slug} className="flex items-start space-x-3 cursor-pointer">
                  <input
                    type="checkbox"
                    disabled={busy}
                    checked={access.permissions.includes(permission.permission_slug)}
                    onChange={(e) => togglePermission(permission.permission_slug, e.target.checked)}
                    className="mt-0.5 focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded"
                  />
                  <span className="text-sm text-gray-700">
                    {permission.permission_description || permission.permission_slug}
                    <span className="ml-2 text-xs text-gray-400">{permission.permission_slug}</span>
                  </span>
                </label>
              ))}
            </div>
          </div>
        )}

        <div>
          <h3 className="text-sm font-semibold text-gray-900 mb-2">Tilknyttede events</h3>
          {access.events.length === 0 ? (
            <p className="text-sm text-gray-500">Ingen events.</p>
          ) : (
            <ul className="divide-y divide-gray-100 rounded-md border border-gray-200">
              {access.events.map((event) => (
                <li key={event.slug} className="flex items-center justify-between px-4 py-2 text-sm">
                  <span className="text-gray-900">{event.name}</span>
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => removeEvent(event.slug)}
                    className="text-red-600 hover:text-red-800 disabled:opacity-50"
                  >
                    Fjern
                  </button>
                </li>
              ))}
            </ul>
          )}
          <div className="mt-3 flex flex-col sm:flex-row gap-3">
            <select
              value={eventToAdd}
              disabled={busy}
              onChange={(e) => setEventToAdd(e.target.value)}
              className="block w-full sm:flex-1 rounded-md border-gray-300 text-sm focus:border-indigo-500 focus:ring-indigo-500"
            >
              <option value="">Vælg event at tilknytte</option>
              {access.all_events.filter((e) => !attached.has(e.slug)).map((event) => (
                <option key={event.slug} value={event.slug}>{event.name}</option>
              ))}
            </select>
            <button
              type="button"
              onClick={addEvent}
              disabled={busy || !eventToAdd}
              className="inline-flex justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              Tilknyt
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
